import {ReactNode, useEffect, useRef, useState} from 'react'
import './invoicePreview.css'
import {decimalToLocalizedString} from '../../../../../lib/Money.ts'
import {InvoiceLineItem, unitOptionLabel} from './types.ts'
import {
    formatDutchInvoiceDate,
    formatQuantity,
    InvoiceTotals,
    isLineFilled,
    lineTotal,
    pdfVatLabel,
} from './utils/invoiceLines.ts'

export type InvoicePreviewSeller = {
    businessName: string
    streetWithHouseNumber: string
    postalCode: string
    city: string
    website: string | null
    kvkNumber: string | null
    vatNumber: string | null
    contactName: string
    contactEmail: string
    logoDataUri: string | null
    iban: string | null
    ibanOwnerName: string | null
}

export type InvoicePreviewProps = {
    seller: InvoicePreviewSeller
    buyerName: string
    buyerStreetWithHouseNumber: string
    buyerPostalCode: string
    buyerCity: string
    invoiceNumber: string
    invoiceDateISO: string
    paymentTermDays: number
    purchaseOrderReference: string
    customerSpecificReference: string
    lines: InvoiceLineItem[]
    totals: InvoiceTotals
    footerText: string
}

const SHEET_WIDTH_PX = 800

function Placeholder({text}: { text: string }) {
    return <span className="preview-placeholder">{text}</span>
}

function orPlaceholder(value: string, placeholder: string): ReactNode {
    return value.trim() ? value : <Placeholder text={placeholder}/>
}

function postalCityLine(postalCode: string, city: string, placeholder: string): ReactNode {
    if (!postalCode.trim() && !city.trim()) {
        return <Placeholder text={placeholder}/>
    }
    return `${postalCode}, ${city}`
}

function euro(amount: number): string {
    return `€ ${decimalToLocalizedString(amount)}`
}

export default function InvoicePreview({
    seller,
    buyerName,
    buyerStreetWithHouseNumber,
    buyerPostalCode,
    buyerCity,
    invoiceNumber,
    invoiceDateISO,
    paymentTermDays,
    purchaseOrderReference,
    customerSpecificReference,
    lines,
    totals,
    footerText,
}: InvoicePreviewProps) {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const sheetRef = useRef<HTMLDivElement>(null)
    const [scale, setScale] = useState(1)
    const [scaledHeight, setScaledHeight] = useState<number | null>(null)

    useEffect(() => {
        function updateScale() {
            const wrapper = wrapperRef.current
            const sheet = sheetRef.current
            if (!wrapper || !sheet) return
            const nextScale = Math.min(1, wrapper.clientWidth / SHEET_WIDTH_PX)
            setScale(nextScale)
            setScaledHeight(sheet.offsetHeight * nextScale)
        }

        updateScale()
        const observer = new ResizeObserver(updateScale)
        if (wrapperRef.current) observer.observe(wrapperRef.current)
        if (sheetRef.current) observer.observe(sheetRef.current)
        return () => observer.disconnect()
    }, [])

    const filledLines = lines.filter(isLineFilled)
    const invoiceDate = formatDutchInvoiceDate(invoiceDateISO)
    const hasReferences = purchaseOrderReference.trim() !== '' || customerSpecificReference.trim() !== ''

    return (
        <div ref={wrapperRef} className="invoice-preview" style={{height: scaledHeight ?? undefined}}>
            <div
                ref={sheetRef}
                className="invoice-container"
                style={{width: SHEET_WIDTH_PX, transform: `scale(${scale})`, transformOrigin: 'top left'}}
                aria-label="Voorbeeld van de factuur"
            >
                <div className="header">
                    <div className="logo">
                        {seller.logoDataUri &&
                            <img src={seller.logoDataUri} alt="" style={{maxHeight: 72, maxWidth: 300}}/>}
                    </div>
                    <h1 className="invoice-title">Factuur</h1>
                </div>

                <div className="info-section">
                    <div className="client-info">
                        <h3>Klant</h3>
                        <div className="info-box">
                            <p><strong>{orPlaceholder(buyerName, 'Naam van je klant')}</strong></p>
                            <p>{orPlaceholder(buyerStreetWithHouseNumber, 'Straat en huisnummer')}</p>
                            <p>{postalCityLine(buyerPostalCode, buyerCity, 'Postcode en plaats')}</p>
                        </div>
                    </div>

                    <div className="supplier-info">
                        <h3>Leverancier</h3>
                        <div className="info-box">
                            <p><strong>{seller.businessName}</strong></p>
                            <p>{orPlaceholder(seller.streetWithHouseNumber, 'Straat en huisnummer')}</p>
                            <p>{postalCityLine(seller.postalCode, seller.city, 'Postcode en plaats')}</p>
                            {seller.website && (
                                <>
                                    <br/>
                                    <p>{seller.website}</p>
                                </>
                            )}
                            <p style={{marginTop: 10}}><strong>KVK</strong> {orPlaceholder(seller.kvkNumber ?? '', '—')}</p>
                            <p><strong>BTW</strong> {orPlaceholder(seller.vatNumber ?? '', '—')}</p>
                        </div>

                        <h3 className="contact-person-title">Contactpersoon</h3>
                        <div className="info-box">
                            <p><strong>{seller.contactName}</strong></p>
                            <p>{seller.contactEmail}</p>
                        </div>
                    </div>
                </div>

                <div className="invoice-details">
                    <h2>Factuurnummer {invoiceNumber}</h2>
                    <p className="invoice-date">Factuurdatum: {invoiceDate || <Placeholder text="dd-mm-jjjj"/>}</p>
                </div>

                {hasReferences && (
                    <div className="references">
                        {purchaseOrderReference.trim() && (
                            <p><strong>Referentie:</strong> {purchaseOrderReference}</p>
                        )}
                        {customerSpecificReference.trim() && (
                            <p><strong>Kenmerk:</strong> {customerSpecificReference}</p>
                        )}
                    </div>
                )}

                {filledLines.length > 0 ? (
                    <table className="line-items">
                        <thead>
                            <tr>
                                <th className="line-description">Omschrijving</th>
                                <th className="line-quantity">Aantal</th>
                                <th className="line-unit-price">Stukprijs</th>
                                <th className="line-vat">BTW</th>
                                <th className="line-total">Totaal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filledLines.map(line => (
                                <tr key={line.id}>
                                    <td className="line-description">
                                        {orPlaceholder(line.description, 'Omschrijving')}
                                    </td>
                                    <td className="line-quantity">
                                        {formatQuantity(line.quantity ?? 0)} {unitOptionLabel(line.unit)}
                                    </td>
                                    <td className="line-unit-price">{euro(line.unitPrice ?? 0)}</td>
                                    <td className="line-vat">{pdfVatLabel(line.vat)}</td>
                                    <td className="line-total">{euro(lineTotal(line))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="description">
                        <p><Placeholder text="Omschrijving van de geleverde diensten of producten"/></p>
                    </div>
                )}

                <div className="payment-totals-section">
                    <div className="payment-section">
                        <h3>Betaling</h3>
                        <p>
                            Totaal van <strong>{euro(totals.totalInclVat)}</strong>, binnen{' '}
                            <strong>{paymentTermDays} dagen</strong> betalen aan{' '}
                            <strong>{seller.iban ?? <Placeholder text="jouw bankrekening"/>}</strong>
                            {seller.ibanOwnerName ? ` (${seller.ibanOwnerName})` : ''} met omschrijving{' '}
                            <strong>{invoiceNumber}</strong>. <strong>Bedankt!</strong>
                        </p>
                    </div>

                    <div className="totals-section">
                        <div className="totals-row">
                            <span>Totaal excl. BTW</span>
                            <span>{euro(totals.totalExclVat)}</span>
                        </div>
                        {totals.vatBreakdown.map(entry => (
                            <div className="totals-row vat-row" key={entry.vat}>
                                <span>BTW ({pdfVatLabel(entry.vat)})</span>
                                <span>{euro(entry.amount)}</span>
                            </div>
                        ))}
                        <div className="totals-row total">
                            <span>Totaal te betalen</span>
                            <span>{euro(totals.totalInclVat)}</span>
                        </div>
                    </div>
                </div>

                {footerText.trim() && (
                    <div className="invoice-footer-text">
                        <p>{footerText}</p>
                    </div>
                )}
            </div>
        </div>
    )
}
